import { useCallback, useEffect, useMemo, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { endpointService } from "../services/endpointService";

interface UseWhatsAppEndpointParams {
  organizationId?: string;
}

interface UseWhatsAppEndpointResult {
  wabaIdRank1: string | null;
  phoneNumberIdRank1: string | null;
  isLoading: boolean;
  errorMessage: string | null;
  refresh: () => Promise<void>;
}

/**
 * useWhatsAppEndpoint
 *
 * Hook de domínio para resolver configurações necessárias para envio (WABA id).
 * Busca o Endpoint ativo no FHIR e extrai o WABA do contato rank 1.
 */
export function useWhatsAppEndpoint(
  params: UseWhatsAppEndpointParams = {},
): UseWhatsAppEndpointResult {
  const [wabaIdRank1, setWabaIdRank1] = useState<string | null>(null);
  const [phoneNumberIdRank1, setPhoneNumberIdRank1] = useState<string | null>(
    null,
  );
  /**
   * `useAsyncTask` inicia em `idle` (loading false) até `run()` ser chamado.
   * Para callers que precisam "gating" (ex.: templates), isso criava uma janela
   * no primeiro render onde `isLoading` parecia false e liberava busca DEFAULT,
   * causando o efeito: DEFAULT → endpoint (double-fetch / UI piscando).
   *
   * Mantemos um sinal explícito para considerar "carregando" até o primeiro `runLoad`
   * ser disparado.
   */
  const [hasTriggeredInitialLoad, setHasTriggeredInitialLoad] = useState(false);

  const stableParams = useMemo(
    () => ({ organizationId: params.organizationId }),
    [params.organizationId],
  );

  const {
    run: runLoad,
    loading: isLoading,
    error,
  } = useAsyncTask<[], { wabaIdRank1: string | null; phoneNumberIdRank1: string | null }>({
    task: useCallback(async () => {
      const config =
        await endpointService.getWhatsAppEndpointConfigByContext(stableParams);
      setWabaIdRank1(config.wabaIdRank1);
      setPhoneNumberIdRank1(config.phoneNumberIdRank1);
      return config;
    }, [stableParams]),
  });

  const refresh = useCallback(async () => {
    await runLoad();
  }, [runLoad]);

  useEffect(() => {
    setHasTriggeredInitialLoad(true);
    // Evita unhandled rejection (pode interromper fluxos em alguns browsers/builds).
    runLoad().catch(() => {});
  }, [runLoad]);

  return {
    wabaIdRank1,
    phoneNumberIdRank1,
    isLoading: isLoading || !hasTriggeredInitialLoad,
    errorMessage: error ? "Erro ao carregar configurações do WhatsApp." : null,
    refresh,
  };
}

